import type { RefObject } from 'react'
import { useCallback, useEffect, useState } from 'react'

import {
  SliderArrowLeftIcon,
  SliderArrowRightIcon,
} from '@twistopayments/design-system/icons/homepage'

import * as styles from './blog-list-controls.styles'

const SCROLL_OFFSET = 360

type BlogListControlsProps = {
  containerRef: RefObject<HTMLDivElement>
}

export const BlogListControls = ({ containerRef }: BlogListControlsProps) => {
  const scrollSliderBy = (offset: number) => {
    containerRef.current?.scrollBy({ left: offset, behavior: 'smooth' })
  }

  const handleNext = () => {
    scrollSliderBy(SCROLL_OFFSET)
  }

  const handlePrev = () => {
    scrollSliderBy(-SCROLL_OFFSET)
  }

  const [canScrollPrev, setCanScrollPrev] = useState(false)
  const [canScrollNext, setCanScrollNext] = useState(false)

  const updateScrollButtons = useCallback(() => {
    if (!containerRef.current) {
      return
    }

    const { scrollLeft, scrollWidth, clientWidth } = containerRef.current

    setCanScrollPrev(scrollLeft > 0)
    setCanScrollNext(scrollLeft + clientWidth < scrollWidth)
  }, [containerRef])

  useEffect(() => {
    const scrollableContainer = containerRef.current

    if (!scrollableContainer) {
      return
    }

    updateScrollButtons()

    scrollableContainer.addEventListener('scroll', updateScrollButtons)
    window.addEventListener('resize', updateScrollButtons)

    return () => {
      scrollableContainer.removeEventListener('scroll', updateScrollButtons)
      window.removeEventListener('resize', updateScrollButtons)
    }
  }, [containerRef, updateScrollButtons])

  return (
    <div css={styles.controls}>
      <button disabled={!canScrollPrev} onClick={handlePrev}>
        <SliderArrowLeftIcon />
      </button>
      <button disabled={!canScrollNext} onClick={handleNext}>
        <SliderArrowRightIcon />
      </button>
    </div>
  )
}
